import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  Tabs, 
  Tab, 
  Chip,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert
} from '@mui/material';
import { NavigateNext, History, Link as LinkIcon, Timeline as TimelineIcon, CompareArrows } from '@mui/icons-material';
import VersionTimeline from '../../components/VersionTimeline';
import LinkingVisualization from '../../components/LinkingVisualization';
import ComparisonView from './components/ComparisonView';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API_BASE = `${API_URL}/api/v1`;

/**
 * Audit Page - Version History & Linking Visualization
 * 
 * Shows:
 * - Tab 1: Version History (VersionTimeline component)
 * - Tab 2: Linked Versions (LinkingVisualization component)
 * - Tab 3: Change Causality (future - shows rotation change → timecard change impact)
 * 
 * URL Params:
 * - /audit?type=rotation&number=3599 (query by rotation/timecard number)
 * - /audit/rotation/123 (direct version ID)
 */
const AuditPage = () => {
  const { entityType: urlEntityType, entityId: urlEntityId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // State
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [entityMetadata, setEntityMetadata] = useState(null);

  // Entity type and ID from URL
  const entityType = urlEntityType || searchParams.get('type');
  const entityNumber = searchParams.get('number');
  const entityId = urlEntityId;

  // Fetch versions when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      console.log('🔍 AUDIT PAGE: Starting data fetch');
      console.log('🔍 AUDIT PAGE: Entity type:', entityType);
      console.log('🔍 AUDIT PAGE: Entity number:', entityNumber);
      console.log('🔍 AUDIT PAGE: Entity ID:', entityId);

      try {
        let response;
        let url;
        
        if (entityNumber) {
          // Query by rotation/timecard number - FIXED: Use axios directly with correct endpoint
          if (entityType === 'rotation') {
            url = `${API_BASE}/audit/rotation/${entityNumber}/versions`;
            console.log('🔍 AUDIT PAGE: Fetching rotation versions from:', url);
            response = await axios.get(url);
          } else if (entityType === 'timecard') {
            url = `${API_BASE}/audit/timecard/${entityNumber}/versions`;
            console.log('🔍 AUDIT PAGE: Fetching timecard versions from:', url);
            response = await axios.get(url);
          }
        } else if (entityId) {
          // Query by version ID (less common)
          if (entityType === 'rotation') {
            url = `${API_BASE}/audit/rotation-version/${entityId}`;
            console.log('🔍 AUDIT PAGE: Fetching rotation version by ID from:', url);
            response = await axios.get(url);
            // Wrap single version in array
            response.data = [response.data];
          } else if (entityType === 'timecard') {
            url = `${API_BASE}/audit/timecard-version/${entityId}`;
            console.log('🔍 AUDIT PAGE: Fetching timecard version by ID from:', url);
            response = await axios.get(url);
            response.data = [response.data];
          }
        }

        console.log('✅ AUDIT PAGE: Response received:', response);
        console.log('✅ AUDIT PAGE: Response status:', response?.status);
        console.log('✅ AUDIT PAGE: Response data:', response?.data);

        if (response?.data) {
          const versionList = Array.isArray(response.data) ? response.data : [response.data];
          console.log('✅ AUDIT PAGE: Processed version list:', versionList);
          setVersions(versionList);
          
          // Find current version (highest version_number or is_current flag)
          const current = versionList.find(v => v.is_current) || versionList[0];
          console.log('✅ AUDIT PAGE: Current version:', current);
          setCurrentVersion(current);
          
          // Build metadata for components
          const metadata = {
            number: entityNumber || current?.rotation_number || current?.timecard_number,
            version_number: current?.version_number,
            employee_id: current?.employee_id
          };
          console.log('✅ AUDIT PAGE: Entity metadata:', metadata);
          setEntityMetadata(metadata);
        }
      } catch (err) {
        console.error('❌ AUDIT PAGE: Error occurred:', err);
        console.error('❌ AUDIT PAGE: Error message:', err.message);
        console.error('❌ AUDIT PAGE: Error response:', err.response);
        console.error('❌ AUDIT PAGE: Error response status:', err.response?.status);
        console.error('❌ AUDIT PAGE: Error response data:', err.response?.data);
        console.error('❌ AUDIT PAGE: Error response headers:', err.response?.headers);
        console.error('❌ AUDIT PAGE: Error config:', err.config);
        
        const errorMsg = err.response?.data?.detail || err.message || 'Failed to load audit data';
        console.error('❌ AUDIT PAGE: Setting error message:', errorMsg);
        setError(errorMsg);
      } finally {
        console.log('🔍 AUDIT PAGE: Fetch complete, setting loading to false');
        setLoading(false);
      }
    };

    if (entityType && (entityNumber || entityId)) {
      console.log('🔍 AUDIT PAGE: All required params present, starting fetch');
      fetchData();
    } else {
      console.error('❌ AUDIT PAGE: Missing required parameters');
      console.error('❌ AUDIT PAGE: entityType:', entityType);
      console.error('❌ AUDIT PAGE: entityNumber:', entityNumber);
      console.error('❌ AUDIT PAGE: entityId:', entityId);
      setError('Missing required parameters: type and number/id');
      setLoading(false);
    }
  }, [entityType, entityNumber, entityId]);

  // Handle version comparison
  const handleCompare = (version1, version2) => {
    // TODO: Compare functionality - page not yet implemented
    console.log('Compare requested:', version1, version2);
    alert('Version comparison feature coming soon!\n\nThis will show a side-by-side diff of the two versions.');
    
    // const type = entityType === 'rotation' ? 'rotation' : 'timecard';
    // // Use rotation_number/timecard_number instead of database ID
    // const id1 = version1.rotation_number || version1.timecard_number || version1.id;
    // const id2 = version2.rotation_number || version2.timecard_number || version2.id;
    // navigate(`/compare/${type}/${id1}/${id2}`);
  };

  // Handle navigation to linked entity
  const handleLinkedEntityNavigate = (target) => {
    navigate(`/audit?type=${target.type}&number=${target.number}`);
  };

  // Check if spillover
  const isSpillover = entityType === 'rotation' && versions.some(v => {
    // Check if any version has spillover flag in metadata
    return v.link_method === 'spillover' || v.metadata?.spillover === true;
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 3 }}>
        <Link 
          underline="hover" 
          color="inherit" 
          onClick={() => navigate('/data-management')}
          sx={{ cursor: 'pointer' }}
        >
          Data Management
        </Link>
        <Link 
          underline="hover" 
          color="inherit" 
          onClick={() => navigate(`/data-management?tab=${entityType === 'rotation' ? 0 : 1}`)}
          sx={{ cursor: 'pointer' }}
        >
          {entityType === 'rotation' ? 'Rotations' : 'Timecards'}
        </Link>
        <Typography color="text.primary">
          Audit: {entityMetadata?.number || 'N/A'}
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <History fontSize="large" color="primary" />
          <Typography variant="h4">
            Version Audit
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label={entityType === 'rotation' ? 'ROTATION' : 'TIMECARD'} 
            color="primary" 
            size="small" 
          />
          <Typography variant="h6" color="text.secondary">
            {entityMetadata?.number || 'Unknown'}
          </Typography>
          {entityMetadata?.version_number && (
            <Chip 
              label={`Version ${entityMetadata.version_number}`} 
              variant="outlined" 
              size="small" 
            />
          )}
          {isSpillover && (
            <Chip 
              label="SPILLOVER" 
              color="warning" 
              size="small" 
              icon={<LinkIcon />}
            />
          )}
        </Box>

        {currentVersion && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Employee: {currentVersion.employee_id} • 
            {versions.length} version{versions.length !== 1 ? 's' : ''} tracked
          </Typography>
        )}
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="fullWidth"
        >
          <Tab 
            icon={<TimelineIcon />} 
            label="Version History" 
            iconPosition="start" 
          />
          <Tab 
            icon={<LinkIcon />} 
            label="Linked Versions" 
            iconPosition="start" 
          />
          <Tab 
            icon={<CompareArrows />} 
            label="Compare Versions" 
            iconPosition="start" 
          />
        </Tabs>
      </Paper>

      {/* Tab panels */}
      <Box>
        {/* Tab 1: Version History */}
        {tabValue === 0 && (
          <Box>
            {versions.length > 0 ? (
              <VersionTimeline
                entityType={entityType}
                entityId={currentVersion?.id}
                versions={versions}
                onCompare={handleCompare}
              />
            ) : (
              <Paper sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No version history available for this {entityType}.
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {/* Tab 2: Linked Versions */}
        {tabValue === 1 && (
          <Box>
            {currentVersion ? (
              <LinkingVisualization
                sourceType={entityType}
                sourceVersionId={currentVersion.id}
                sourceMetadata={entityMetadata}
                onNavigate={handleLinkedEntityNavigate}
              />
            ) : (
              <Paper sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No current version found for linking visualization.
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {/* Tab 3: Compare Versions */}
        {tabValue === 2 && (
          <Box>
            {versions.length >= 2 ? (
              <ComparisonView
                entityType={entityType}
                versions={versions}
              />
            ) : (
              <Paper sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Need at least 2 versions to compare. Upload more versions to enable comparison.
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </Box>

      {/* Future: Causality tab (showing rotation change → timecard change impacts) */}
    </Container>
  );
};

export default AuditPage;
